<template>
    <div    class="w-full flex flex-col items-center">
        <!-- Probe Count Summary -->
        <div    class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 w-2/3 justify-center">
            <!-- This example requires Tailwind CSS v2.0+ -->
            <div    v-for="(count, typeName) in probeCounts"
                    class="my-4">
                <!-- Card -->
                <div class="relative mx-8 rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm flex shrink-0 items-center space-x-3 hover:border-gray-400 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                    <div class="shrink-0">
                        <span   class="text-md">{{ typeName }}</span>
                    </div>
                    <div class="shrink-0 min-w-0">
                        <span   class="text-md font-semibold">{{ count }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Companies Being Loaded -->
        <div    class="w-2/3 my-4">
            <install-wizard-well v-if="loadingCompanies.length > 0">
                <template #well-header-left>
                    <span>Loading Company</span>
                </template>

                <template #well-header-center>
                    <span>{{ loadingCompanies[0] }}</span>
                </template>

                <template #well-content>
                    <span   class="font-semibold">Remaining</span>
                    <ul>
                        <li     v-for="company in loadingCompanies">
                            {{ company }}
                        </li>
                    </ul>
                </template>
            </install-wizard-well>
        </div>

        <div    class="mb-8 w-2/3">

            <download-excel
                    v-if="loadingCompanies.length === 0"
                    :fetch="exportData"
                    :fields="csvFields"
                    type="csv">
                <span v-if="exporting"
                      class="block items-center ml-2 py-1 px-8  pl-3 border-2 border-blue-200 text-md leading-5 font-sm
                        font-sans rounded-md text-blue-700 bg-gray-200 focus:outline-none
                        cursor-pointer flex flex-row items-center justify-center"
                >
                    <span :class="iconLoading"></span>
                    <span>Exporting</span>
                </span>

                <button v-else
                        class="w-full relative relative items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-500 focus:outline-none focus:ring-blue-200 focus:border-blue-700 active:bg-blue-700">
                    Export All Data
                </button>
            </download-excel>

            <button     v-else
                                         class="w-full cursor-not-allowed relative items-center px-4 py-2 border border-transparent text-sm text-center leading-5 font-medium rounded-md text-gray-100 bg-gray-500 ">
            Wait for Data to Load
        </button>
        </div>


        <!-- Probe Table -->
        <div    v-if="probes.length < 1000"
                class="w-5/6 flex flex-col">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Company
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Area / Zone
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Point
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Probe
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Snap
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Calibration
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <!-- Odd row -->
                            <tr     v-for="(probe, index) in probes"
                                    class="bg-white"
                                    :class="[index % 2 === 0 ? 'bg-white' : 'bg-gray-50']">
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {{ probe.company }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                    {{ probe.area }} / {{ probe.zone }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ probe.name }} ( {{ probe.pointSerial }})
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ probe.probeType }} ({{ probe.probeSerial }})
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ probe.snapEnrollment }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {{ probe.calibrationDate }}
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import { iconNamesMixin } from "../mixins/IconNamesMixin";
import InstallWizardWell from "./InstallWizard/InstallWizardWell"


import JsonExcel from './JsonExcel'
import axios from "axios";

export default {
    name: "ProbeCalibrationAudit",
    mixins: [SonicuCoreMixin, iconNamesMixin],
    components: {InstallWizardWell, 'download-excel' : JsonExcel},
    props: {
        companies: Array,
    },
    data() {
        return {
            probeCounts: {},
            probes: [],
            loadingCompanies: [],
            csvFields: {
                'Company': 'company',
                'Address' : 'address',
                'Area' : 'area',
                'Zone' : 'zone',
                'Point Name' : 'name',
                'Point Serial Number' : 'pointSerial',
                'Probe Type' : 'probeType',
                'Probe Serial' : 'probeSerial',
                'Snap Enrollment' : 'snapEnrollment',
                'Calibration Date' : 'calibrationDate',
            },
            exporting: false,
        }
    },
    created() {
        this.loadingCompanies = this.companies
    },
    mounted() {
        this.probes = []
        this.probeCounts = {}
        this.loadingCompanies.forEach((item, index) => {
            axios.get(this.company.buildCompanyApiInternalRoute(`probe-calibration-audit/${item}`)).then(
                response => {
                    this.loadingCompanies.splice(this.loadingCompanies.indexOf(item), 1)
                    // this.probeCounts = response.data.probeCounts
                    // this.probes = response.data.probes
                    if (!_.isNil(response.data.probeCounts)) {
                        _.forIn(response.data.probeCounts, (count, type) => {
                            if (!(type in this.probeCounts)) {
                                this.probeCounts[type] = 0
                            }
                            this.probeCounts[type] += count
                        })
                    }

                    if (!_.isNil(response.data.probes)) {
                        _.forIn(response.data.probes, (probe, key) => {
                            this.probes.push(probe)
                        })
                    }

                }
            )
        })
    },
    methods: {
        // This is cool, makes sure the request is inline, only issue is it could hold up the page for long requests
        async exportData() {
            // Incase this is just super nasty to store al on local, use this to send a
            // AJAX request to get it.

            // this.exporting = true
            // let params = {
            //     page: 1,
            //     perPage: this.pageInfo.total.toString(),
            //     search: this.search,
            //     sortBy: this.sortBy,
            //     sortDesc: this.sortDesc,
            //     showActive: this.showActive,
            //     showSnoozed: this.showSnoozed,
            //     showResolved: this.showResolved,
            //     startTime: this.startTime !== null ? `${this.startTime.toISOString()}` : null,
            //     endTime: this.endTime !== null ? `${this.endTime.toISOString()}` : null,
            // }
            //
            // let newData = []
            // const response = await axios.get(this.company.buildCompanyApiInternalRoute('alarms/'), {
            //     params: params
            // })
            //
            // newData = response.data.data !== undefined ? response.data.data : []
            // this.exporting = false

            return this.probes
        },
    },
    computed: {

    },
}
</script>

<style scoped>

</style>